import type { TaskSnapshot } from '../../../../stores/types';
import { isPlanningShellTask } from '../../../../gantt/addTaskUtils';
import { parseDateSafe } from '../../../../utils/utils';

/**
 * DPR Targets-style overlap vs a look-ahead window:
 * - both planned start & end: overlap if start < windowEnd && finish > windowStart
 * - start only (no end): include if start <= windowEnd
 * - missing both: exclude
 */
export function taskOverlapsLookAheadWindow(
  task: Pick<TaskSnapshot, 'start' | 'finish'>,
  windowStart: Date,
  windowEnd: Date,
): boolean {
  const start = parseDateSafe(task.start);
  const finish = parseDateSafe(task.finish);

  if (start && finish) {
    return start.getTime() < windowEnd.getTime() && finish.getTime() > windowStart.getTime();
  }
  if (start && !finish) {
    return start.getTime() <= windowEnd.getTime();
  }
  return false;
}

/** Location / bootstrap shells are WBS structure — not look-ahead work leaves. */
function isLookAheadStructuralTask(task: TaskSnapshot, allTasks: TaskSnapshot[]): boolean {
  if (isPlanningShellTask(task, allTasks)) return true;
  const type = String(task.taskType ?? '').trim().toLowerCase();
  return type === 'location';
}

function addWithAncestors(
  keep: Set<string>,
  task: TaskSnapshot,
  byId: Map<string, TaskSnapshot>,
): void {
  const id = String(task.id);
  keep.add(id);
  let parentId = task.parentTaskId ? String(task.parentTaskId) : null;
  while (parentId) {
    if (keep.has(parentId)) break;
    keep.add(parentId);
    const parent = byId.get(parentId);
    parentId = parent?.parentTaskId ? String(parent.parentTaskId) : null;
  }
}

/**
 * Leaf task ids whose schedule overlaps the look-ahead window (DPR date rules).
 * Skips planning shells and location-type nodes (hierarchy only).
 */
export function collectLookAheadLeafTaskIds(
  allTasks: TaskSnapshot[],
  windowStart: Date,
  windowEnd: Date,
): Set<string> {
  const parentIds = new Set<string>();
  for (const t of allTasks) {
    if (t.parentTaskId) parentIds.add(String(t.parentTaskId));
  }

  const keep = new Set<string>();
  for (const t of allTasks) {
    const id = String(t.id);
    if (parentIds.has(id)) continue;
    if (isLookAheadStructuralTask(t, allTasks)) continue;
    if (!taskOverlapsLookAheadWindow(t, windowStart, windowEnd)) continue;
    keep.add(id);
  }
  return keep;
}

/**
 * Targets-mode tree: look-ahead leaves + ancestors needed for context.
 * Leaves outside the window are omitted.
 */
export function collectTargetsModeVisibleTaskIds(
  allTasks: TaskSnapshot[],
  windowStart: Date,
  windowEnd: Date,
): Set<string> {
  const byId = new Map(allTasks.map((t) => [String(t.id), t]));
  const leafIds = collectLookAheadLeafTaskIds(allTasks, windowStart, windowEnd);
  const keep = new Set<string>();
  for (const id of leafIds) {
    const task = byId.get(id);
    if (task) addWithAncestors(keep, task, byId);
  }
  return keep;
}

/** @deprecated Prefer {@link collectLookAheadLeafTaskIds}. */
export function collectLookAheadTaskIds(
  allTasks: TaskSnapshot[],
  windowStart: Date,
  windowEnd: Date,
): Set<string> {
  return collectLookAheadLeafTaskIds(allTasks, windowStart, windowEnd);
}

/**
 * Visible row order matching DHTMLX with `open` / collapsed branches.
 */
export function getVisibleTargetRowTaskIds(
  orderedTaskIds: string[],
  parentById: Map<string, string | null>,
  collapsedBranchIds: Set<string>,
): string[] {
  const visible: string[] = [];
  for (const id of orderedTaskIds) {
    let ancestor = parentById.get(id) ?? null;
    let hidden = false;
    while (ancestor) {
      if (collapsedBranchIds.has(ancestor)) {
        hidden = true;
        break;
      }
      ancestor = parentById.get(ancestor) ?? null;
    }
    if (!hidden) visible.push(id);
  }
  return visible;
}
